import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  Input,
  VStack,
  useToast,
  Text,
} from '@chakra-ui/react';
import { putData, getData } from '../../utils/httpClient.js';

const EditRevalidationModal = ({ isOpen, onClose, revalidation, onSuccess }) => {
  const [formData, setFormData] = useState({
    studentId: '',
    subjectId: '',
    originInstitute: '',
    acceptanceDate: '',
  });
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [showCustomInstitute, setShowCustomInstitute] = useState(false);
  const [customInstitute, setCustomInstitute] = useState('');
  const toast = useToast();

  // Cargar datos necesarios para los selectores
  useEffect(() => {
    if (isOpen && !revalidation) {
      // Solo cargar datos generales si no hay reválida específica
      loadInitialData();
    }
  }, [isOpen, revalidation]);

  // Cargar datos del revalidation a editar
  useEffect(() => {
    if (revalidation && isOpen) {
      loadRevalidationDetails();
    }
  }, [revalidation, isOpen]);

  // Nueva función para cargar los detalles completos de la reválida
  const loadRevalidationDetails = async () => {
    if (!revalidation?.id) return;
    
    setLoadingData(true);
    try {
      // Cargar datos en paralelo
      const [revalidationDetailsResponse, studentsResponse, subjectsResponse] = await Promise.all([
        getData(`/revalidations/${revalidation.id}`),
        getData('/person/student?limit=1000'),
        getData('/subjects?limit=1000'),
      ]);

      console.log('Revalidation details response:', revalidationDetailsResponse);
      console.log('Students response:', studentsResponse);
      console.log('Subjects response:', subjectsResponse);

      // Procesar respuestas
      const revalidationData = revalidationDetailsResponse?.payload?.revalidation;
      const studentsData = studentsResponse?.payload?.people || [];
      const subjectsData = subjectsResponse?.payload?.subjects || subjectsResponse?.payload || [];

      setStudents(studentsData);
      setSubjects(subjectsData);

      // Cargar instituciones
      try {
        const institutionsResponse = await getData('/institutions?country=Uruguay&enabled=true');
        console.log('Institutions response:', institutionsResponse);
        const institutionsData = institutionsResponse?.payload || [];
        setInstitutions(Array.isArray(institutionsData) ? institutionsData : []);
      } catch (institutionError) {
        console.warn('Instituciones endpoint not available yet:', institutionError);
        setInstitutions([]);
      }

      // Una vez que tenemos todos los datos, inicializar el formulario
      if (revalidationData) {
        initializeFormWithData(revalidationData, studentsData, subjectsData);
      }
    } catch (error) {
      console.error('Error loading revalidation details:', error);
      toast({
        title: 'Error al cargar datos de la reválida',
        description: 'No se pudieron cargar los datos necesarios',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingData(false);
    }
  };

  const initializeFormWithData = (revalidationData, studentsData = [], subjectsData = []) => {
    console.log('Initializing form with full data:', revalidationData);

    setFormData({
      studentId: revalidationData.studentId?.toString() || '',
      subjectId: revalidationData.subjectId?.toString() || '',
      originInstitute: revalidationData.originInstitute || '',
      acceptanceDate: revalidationData.acceptanceDate ? 
        new Date(revalidationData.acceptanceDate).toISOString().split('T')[0] : '',
    });

    // Verificar si el instituto es personalizado
    const instituteValue = revalidationData.originInstitute || '';
    if (instituteValue && institutions.length > 0) {
      const instituteExists = institutions.some(inst => inst.name === instituteValue);
      if (!instituteExists) {
        setShowCustomInstitute(true);
        setCustomInstitute(instituteValue);
      } else {
        setShowCustomInstitute(false);
        setCustomInstitute('');
      }
    } else if (instituteValue) {
      setShowCustomInstitute(true);
      setCustomInstitute(instituteValue);
    }
  };

  const loadInitialData = async () => {
    setLoadingData(true);
    try {
      const [studentsResponse, subjectsResponse] = await Promise.all([
        getData('/person/student?limit=1000'),
        getData('/subjects?limit=1000'),
      ]);

      console.log('Students response:', studentsResponse);
      console.log('Subjects response:', subjectsResponse);

      // El endpoint de person devuelve 'people', no 'students'
      const studentsData = studentsResponse?.payload?.people || [];
      const subjectsData = subjectsResponse?.payload?.subjects || subjectsResponse?.payload || [];

      setStudents(studentsData);
      setSubjects(subjectsData);

      // Intentar cargar instituciones, pero no fallar si no existe el endpoint
      try {
        const institutionsResponse = await getData('/institutions?country=Uruguay&enabled=true');
        console.log('Institutions response:', institutionsResponse);
        const institutionsData = institutionsResponse?.payload || [];
        setInstitutions(Array.isArray(institutionsData) ? institutionsData : []);
      } catch (institutionError) {
        console.warn('Instituciones endpoint not available yet:', institutionError);
        setInstitutions([]);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast({
        title: 'Error al cargar datos',
        description: 'No se pudieron cargar los datos necesarios',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'originInstitute') {
      if (value === 'Otro Instituto') {
        setShowCustomInstitute(true);
        setFormData(prev => ({ ...prev, [name]: '' }));
      } else {
        setShowCustomInstitute(false);
        setCustomInstitute('');
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCustomInstituteChange = (value) => {
    setCustomInstitute(value);
    setFormData(prev => ({ ...prev, originInstitute: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.studentId || !formData.subjectId || !formData.originInstitute || !formData.acceptanceDate) {
      toast({
        title: 'Campos requeridos',
        description: 'Por favor complete todos los campos',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      // NOTA: No enviamos studentId ya que no se puede modificar
      await putData(`/revalidations/${revalidation.id}`, {
        subjectId: parseInt(formData.subjectId),
        originInstitute: formData.originInstitute,
        acceptanceDate: formData.acceptanceDate,
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating revalidation:', error);
      toast({
        title: 'Error al actualizar reválida',
        description: error.message || 'Ha ocurrido un error inesperado',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Limpiar estados
    setShowCustomInstitute(false);
    setCustomInstitute('');
    onClose();
  };

  if (!revalidation) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Editar Reválida</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            {loadingData ? (
              <Text>Cargando datos...</Text>
            ) : (
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Estudiante</FormLabel>
                  <Select
                    name="studentId"
                    value={formData.studentId}
                    onChange={handleChange}
                    placeholder="Seleccionar estudiante"
                    isDisabled={true}
                    bg="gray.100"
                    cursor="not-allowed"
                  >
                    {students.map((person) => (
                      <option key={person.student?.id} value={person.student?.id}>
                        {person.name} {person.lastName} {person.maternalLastName || ''} - {person.document}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    El estudiante no puede ser modificado
                  </Text>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Materia</FormLabel>
                  <Select
                    name="subjectId"
                    value={formData.subjectId}
                    onChange={handleChange}
                    placeholder="Seleccionar materia"
                    isDisabled={loadingData}
                  >
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.description} - Ciclo {subject.cycle}
                      </option>
                    ))}
                  </Select>
                  {formData.subjectId && (
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      {subjects.find(s => s.id === parseInt(formData.subjectId))?.study?.description}
                    </Text>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Instituto de Origen</FormLabel>
                  <Select
                    name="originInstitute"
                    value={showCustomInstitute ? 'Otro Instituto' : formData.originInstitute}
                    onChange={handleChange}
                    placeholder="Seleccione el instituto de origen"
                  >
                    {Array.isArray(institutions) && institutions.map((institucion) => (
                      <option key={institucion.id} value={institucion.name}>
                        {institucion.name}
                      </option>
                    ))}
                    <option value="Otro Instituto">Otro Instituto</option>
                  </Select>
                  {showCustomInstitute && (
                    <Input
                      placeholder="Ingrese el nombre del instituto"
                      value={customInstitute}
                      onChange={(e) => handleCustomInstituteChange(e.target.value)}
                      mt={2}
                      maxLength={500}
                    />
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Fecha de Aceptación</FormLabel>
                  <Input
                    name="acceptanceDate"
                    type="date"
                    value={formData.acceptanceDate}
                    onChange={handleChange}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isDisabled={loadingData}
            >
              Actualizar Reválida
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default EditRevalidationModal;
